// <!-- GRACE:MODULE_START name="ui_landing" -->
// <!-- GRACE:DEPENDENCY target="ui_asr" -->
// <!-- GRACE:DEPENDENCY target="ui_templates" -->
// <!-- GRACE:DEPENDENCY target="ui_display" -->
// <!-- GRACE:DEPENDENCY target="ui_status" -->
import Header from "./Header";
import HeroSection from "./HeroSection";
import Card from "./Card";
import { Template } from "../../services/templateService";

interface ProcessingState {
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  estimatedTime: number;
  operationId: string;
  error: string;
}

interface TemplatePageProps {
  // Header props
  onNavigate: (page: 'facts' | 'template' | 'dialog') => void;
  
  // Hero props
  isRecording: boolean;
  transcription: string;
  processingState: ProcessingState;
  audioUrl: string | null;
  audioBlob: Blob | null;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onTranscriptionChange: (text: string) => void;
  onFileChange: (file: File) => void;
  onClearTranscription: () => void;
  onCopyTranscription: () => void;
  onDownloadTranscription: () => void;
  
  // Template formatting props
  templates: Template[];
  templatesLoading: boolean;
  selectedTemplate: string;
  formattedResult: string;
  formatProcessing: boolean;
  onTemplateSelect: (templateId: string) => void;
  onFormatByTemplate: () => void;
  onCopyFormattedResult: () => void;
  onDownloadFormattedResult: () => void;
}

// <!-- GRACE:CONTRACT function="TemplatePage" -->
export default function TemplatePage({
  onNavigate,
  isRecording,
  transcription,
  processingState,
  audioUrl,
  audioBlob,
  onStartRecording,
  onStopRecording,
  onTranscriptionChange,
  onFileChange,
  onClearTranscription,
  onCopyTranscription,
  onDownloadTranscription,
  templates,
  templatesLoading,
  selectedTemplate,
  formattedResult,
  formatProcessing,
  onTemplateSelect,
  onFormatByTemplate,
  onCopyFormattedResult,
  onDownloadFormattedResult,
}: TemplatePageProps) {
  // Find the selected template object
  const selectedTemplateObj = templates.find(t => t.id === selectedTemplate);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Header activePage="template" onNavigate={onNavigate} />

      {/* Hero */}
      <HeroSection
        isRecording={isRecording}
        transcription={transcription}
        processingState={processingState}
        audioUrl={audioUrl}
        audioBlob={audioBlob}
        onStartRecording={onStartRecording}
        onStopRecording={onStopRecording}
        onTranscriptionChange={onTranscriptionChange}
        onFileChange={onFileChange}
        onClear={onClearTranscription}
        onCopy={onCopyTranscription}
        onDownload={onDownloadTranscription}
      />

      {/* Template Formatting Section */}
      <Card title="Форматирование по шаблону">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Левая колонка: Выбор шаблона и текст шаблона */}
          <div className="space-y-3">
            <div>
              <div className="mb-1 text-sm font-medium text-gray-700">
                Выберите шаблон
              </div>
              {templatesLoading ? (
                <div className="text-sm text-gray-500 py-2">Загрузка шаблонов...</div>
              ) : templates.length === 0 ? (
                <div className="text-sm text-red-500 py-2">Нет доступных шаблонов</div>
              ) : (
                <select
                  value={selectedTemplate}
                  onChange={(e) => onTemplateSelect(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2 text-sm"
                >
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {selectedTemplateObj && (
              <div>
                <div className="mb-1 text-sm font-medium text-gray-700">
                  Описание шаблона
                </div>
                <p className="text-xs text-gray-600 mb-2">{selectedTemplateObj.description}</p>
                <div className="mb-1 text-sm font-medium text-gray-700">
                  Предварительный просмотр шаблона
                </div>
                <textarea
                  readOnly
                  value={selectedTemplateObj.template}
                  className="h-40 w-full resize-y rounded-xl border bg-gray-50 p-3 text-sm font-mono"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Плейсхолдеры ХХХХХ будут заполнены автоматически через GPT
                </p>
              </div>
            )}
            <button
              onClick={onFormatByTemplate}
              disabled={formatProcessing || !transcription.trim() || !selectedTemplate}
              className={`w-full rounded-xl px-4 py-2 text-sm font-medium text-white ${
                formatProcessing || !transcription.trim() || !selectedTemplate
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gray-900 hover:bg-gray-800'
              }`}
            >
              {formatProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Форматирование...
                </span>
              ) : (
                'Форматировать текст'
              )}
            </button>
          </div>

          {/* Правая колонка: Результат форматирования */}
          <div className="space-y-3">
            <div>
              <div className="mb-1 text-sm font-medium text-gray-700">
                Результат форматирования
              </div>
              <textarea
                readOnly
                value={formattedResult}
                className="h-40 w-full resize-y rounded-xl border bg-gray-50 p-3 text-sm"
              />
            </div>
            <div className="flex gap-2 text-xs">
              <button
                onClick={onCopyFormattedResult}
                className="rounded-lg border px-3 py-1.5 hover:bg-gray-50"
              >
                Копировать
              </button>
              <button
                onClick={onDownloadFormattedResult}
                className="rounded-lg border px-3 py-1.5 hover:bg-gray-50"
              >
                Скачать
              </button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
// <!-- GRACE:MODULE_END name="ui_landing" -->
